import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const STYLES = {
  success: { icon: CheckCircle2, iconCls: 'text-ok bg-ok-soft', bar: 'bg-ok' },
  error: { icon: AlertTriangle, iconCls: 'text-danger bg-danger-soft', bar: 'bg-danger' },
  info: { icon: Info, iconCls: 'text-primary bg-sky-soft', bar: 'bg-primary' },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const toast = useCallback(
    (message, type = 'success') => {
      idRef.current += 1;
      const id = idRef.current;
      const ms = type === 'error' ? 5000 : 2600;
      setToasts((t) => [...t.slice(-3), { id, message, type, ms }]);
      setTimeout(() => dismiss(id), ms);
    },
    [dismiss]
  );

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex flex-col items-center gap-2 px-4" aria-live="polite">
        <AnimatePresence initial={false}>
          {toasts.map((t) => {
            const s = STYLES[t.type] || STYLES.info;
            const Icon = s.icon;
            return (
              <motion.div
                layout
                key={t.id}
                initial={{ opacity: 0, y: 24, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.95, transition: { duration: 0.15 } }}
                transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                className="pointer-events-auto relative flex max-w-md items-center gap-3 overflow-hidden rounded-2xl border border-line/25 bg-surface py-2.5 pl-2.5 pr-3 text-ink shadow-lift"
                role={t.type === 'error' ? 'alert' : 'status'}
              >
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-xl ${s.iconCls}`}>
                  <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <span className="text-sm font-semibold">{t.message}</span>
                <button type="button" onClick={() => dismiss(t.id)} className="ml-1 rounded-full p-1 text-muted transition hover:bg-ink/5 hover:text-ink" aria-label="Dismiss">
                  <X className="h-4 w-4" />
                </button>
                <motion.span
                  aria-hidden="true"
                  className={`absolute bottom-0 left-0 h-0.5 w-full origin-left ${s.bar} opacity-60`}
                  initial={{ scaleX: 1 }}
                  animate={{ scaleX: 0 }}
                  transition={{ duration: t.ms / 1000, ease: 'linear' }}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
